import React, { useEffect, useMemo, useRef } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { motion } from "framer-motion";
import { FaTrash } from "react-icons/fa";
import { URDPakkaFields } from "../../config/fieldsHelper";
import { handleDropDowns } from "../../config/dropDownHelpers";
import { useSelector } from "react-redux";
import { threeDecimal, twoDecimal } from "../../config/helperFunctions";
import { useGetModuleFieldsQuery } from "../../toolkitServices/moduleFieldsApi";

// tiny toast
const toast = (msg, type = "error") => {
  if (window?.toastr?.[type]) return window.toastr[type](msg);
  if (window?.toast?.[type]) return window.toast[type](msg);
  if (window?.notify) return window.notify(msg, { type });
  alert(msg);
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "31px",
    height: "31px",
    borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(167,139,250,0.35)" : "none",
    fontSize: "12px",
    backgroundColor: "white",
  }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator: (base) => ({ ...base, padding: "4px" }),
  valueContainer: (base) => ({ ...base, padding: "0 6px", height: "30px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({ ...base, fontSize: "12px" }),
  menuList: (base) => ({ ...base, maxHeight: "80px", overflowY: "auto" }),
  singleValue: (base) => ({ ...base, fontSize: "12px" }),
};

const withErrorBorder = (stylesObj, hasError) => {
  if (!hasError) return stylesObj;
  return {
    ...stylesObj,
    control: (base, state) => {
      const baseCtrl = stylesObj.control
        ? stylesObj.control(base, state)
        : base;
      return {
        ...baseCtrl,
        borderColor: "#ef4444",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(239,68,68,0.20)" : "none",
        "&:hover": { borderColor: "#ef4444" },
      };
    },
  };
};

const inputCls = (hasErr) =>
  `w-full px-3 py-[4.5px] text-sm border rounded-[4px] bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
    hasErr
      ? "border-red-400 focus:ring-red-300 focus:border-red-400"
      : "border-gray-300 focus:ring-violet-300 focus:border-violet-300"
  }`;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const URDItemForm = ({
  control,
  register,
  unregister,
  errors,
  rowindex,
  fieldId,
  onDelete,
  watch,
  setValue,
  getValues,
  showDelete = true,
  cardHeightClass = "h-[40vh] sm:h-[40vh]",
  registerFirstField,
}) => {
  const { data: { data: urdFields = [] } = {}, isLoading: urdFieldsLoading } =
    useGetModuleFieldsQuery("URD_FIELDS");
  const URDPakkaFields = useMemo(
    () => urdFields.filter((f) => f.visibility !== false),
    [urdFields]
  );
  const formRef = useRef(null);

  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);

  const entry = (f) => `urdEntries.${rowindex}.${f}`;
  const get = (k) => Number(getValues(entry(k)) || 0);
  const category = watch(`urdEntries.${rowindex}.category`);

  // recalc (URD)
  const handleCalculations = () => {
    const totalWt = get("totalWt");
    const packingWt = get("packingWt");
    const stoneWt = get("stoneWt");
    const diamondCt = get("diamondWt");
    const finePercent = get("finePercentage");
    const ratePerGram = get("ratePerGram");

    const grossWt = +(totalWt - packingWt).toFixed(3);
    const diamondGram = +(diamondCt * 0.2).toFixed(3);
    const netWt = +(grossWt - stoneWt - diamondGram).toFixed(3);

    const fineWt = +((finePercent / 100) * netWt).toFixed(3);
    const amount = +(fineWt * ratePerGram).toFixed(2);

    setValue(entry("grossWt"), threeDecimal(grossWt));
    setValue(entry("netWt"), threeDecimal(netWt));
    setValue(entry("fineWt"), threeDecimal(fineWt));
    setValue(entry("amount"), twoDecimal(amount));
  };

  // Ctrl+Delete to remove row (same UX)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showDelete) return;
      if (e.ctrlKey && e.key === "Delete") {
        if (
          formRef.current &&
          formRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDelete, showDelete]);

  return (
    <motion.div
      ref={formRef}
      tabIndex={-1}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: rowindex * 0.05 }}
      id={`urd-card-${fieldId}`}
      data-field-id={fieldId}
      className={`urd-card ${cardHeightClass} snap-start px-2 pt-1 rounded-lg border border-gray-200 hover:border-gray-300 shadow-lg bg-white relative overflow-y-auto`}
    >
      {/* header */}
      <div className="mb-0.5 flex items-center justify-between sticky top-0 bg-white pt-1 pb-1">
        <h3 className="text-sm font-semibold text-gray-900">
          #{rowindex + 1} • URD Item
        </h3>
        <div className="flex-1 h-px bg-gray-200 mx-2" />
        {showDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-gray-500 hover:text-red-600 transition-all duration-200 hover:scale-110"
            title="Delete this item"
          >
            <FaTrash className="text-[14px]" />
          </button>
        )}
      </div>

      {/* fields */}
      <div className="grid grid-cols-2 md:grid-cols-12 gap-x-2 gap-y-1">
        {URDPakkaFields?.map((f, i) => {
          const path = entry(f.name);
          const rules = {
            required: f.mandatory ? `${f.label} is required` : false,
          };
          const wideCols = ["urdDescription", "category", "product"].includes(
            f.name
          );
          const colSpan = wideCols ? "md:col-span-2" : "md:col-span-1";
          const hasErr = !!errors?.urdEntries?.[rowindex]?.[f.name];

          if (f.type === "dropdown") {
            const opts = handleDropDowns({
              key: f.name,
              categories: categoriesFromState,
              products: productsFromState,
              category,
            });
            return (
              <div key={f.name} className={colSpan}>
                <label className="block text-xs font-medium text-gray-700 ">
                  {f.label}{" "}
                  {f.mandatory && <span className="text-red-500">*</span>}
                </label>
                <Controller
                  name={path}
                  control={control}
                  rules={rules}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={opts}
                      classNamePrefix="react-select"
                      styles={withErrorBorder(selectStyles, hasErr)}
                      menuPlacement="bottom"
                      ref={(selectRef) => {
                        if (i === 0 && registerFirstField && selectRef) {
                          registerFirstField(fieldId, {
                            focus: () => selectRef?.focus?.(),
                          });
                        }
                      }}
                      isClearable
                      isSearchable
                    />
                  )}
                />
              </div>
            );
          }

          const isCalcTrigger = [
            "totalWt",
            "packingWt",
            "stoneWt",
            "diamondWt",
            "finePercentage",
            "ratePerGram",
          ].includes(f.name);

          return (
            <div key={f.name} className={colSpan}>
              <label className="block text-xs font-medium text-gray-700 ">
                {f.label}{" "}
                {f.mandatory && <span className="text-red-500">*</span>}
              </label>
              <input
                type={f.type === "number" ? "number" : "text"}
                {...register(path, {
                  ...rules,
                  onChange: isCalcTrigger
                    ? () => handleCalculations()
                    : undefined,
                })}
                ref={(el) => {
                  register(path, rules).ref(el);
                  if (i === 0 && registerFirstField)
                    registerFirstField(fieldId, el);
                }}
                placeholder={f.placeholder || ""}
                className={inputCls(hasErr)}
                // lock calculated fields if needed:
                // disabled={["grossWt", "netWt", "fineWt", "amount"].includes(f.name)}
              />
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default URDItemForm;
